import { NextPage } from "next";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { Article, ArticleProps } from "@/components/common/article/Article";
import { Cta } from "@/utils/strapi";
import {
  DescriptiveBlockList,
  DescriptiveBlockListProps,
} from "@/components/common/descriptiveBlockList/DescriptiveBlockList";
import { Section } from "@/components/common/section/Section";
import { CtaButton } from "@/components/common/ctaButton/CtaButton";
import { CategoryBlock } from "@/components/common/categoryBlock/CategoryBlock";
import { CategoryCardProps } from "@/components/common/categoryBlock/categoryCard/CategoryCard";
import {
  AdvantageBlock,
  AdvantageBlockProps,
} from "@/components/common/advantageBlock/AdvantageBlock";
import { FAQProps } from "@/utils/formatCategoryPage";
import { FAQ } from "@/components/pages/vehicle/categoryPage/faq/FAQ";
import styles from "./VTC.module.scss";
import {
  AgencyCardList,
  AgencyCardListProps,
} from "./agencyCardList/AgencyCardList";

export type VTCContent = {
  heroBlock: HeroBlockProps;
  presentation: ArticleProps | undefined;
  cta: Cta | undefined;
  descriptiveBlockList: DescriptiveBlockListProps | undefined;
  categoryPresentation: ArticleProps | undefined;
  categoryCards: CategoryCardProps[] | undefined;
  advantageBlock: AdvantageBlockProps | undefined;
  agencyCardList: AgencyCardListProps | undefined;
  faq?: FAQProps;
  seo: SEO;
  seoWithoutTemplate: SEOWithoutTemplate;
};
type VTCProps = PageProps &
  VTCContent & {
    defaultCanonicalRoute: string;
  };

export const VTC: NextPage<VTCProps> = ({
  layout,
  heroBlock,
  presentation,
  cta,
  descriptiveBlockList,
  categoryPresentation,
  categoryCards,
  advantageBlock,
  agencyCardList,
  faq,
  seo,
  seoWithoutTemplate,
  defaultCanonicalRoute,
}: VTCProps) => {
  const intl = useIntl();

  const onClickCta = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: `vtcBreadcrumb` }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <HeroBlock {...heroBlock} hideSearchBlock={true} />
        <Section>
          {presentation && <Article {...presentation} />}
          {cta && (
            <div className={styles.ctaMargin}>
              <CtaButton
                label={cta.label}
                onClick={() => onClickCta(cta.url)}
              />
            </div>
          )}
          {descriptiveBlockList && (
            <DescriptiveBlockList {...descriptiveBlockList} />
          )}
          {categoryPresentation && <Article {...categoryPresentation} />}
          {categoryCards && <CategoryBlock categoryCards={categoryCards} />}
        </Section>
        {advantageBlock && <AdvantageBlock {...advantageBlock} />}
        <Section>
          {agencyCardList && (
            <AgencyCardList {...agencyCardList} openInNewTab />
          )}
        </Section>

        {faq && <FAQ {...faq} />}
      </Layout>
    </>
  );
};
